<template>
    <Toast :position="toastPosition" :onClick="handleClick">
        <template #message="slotProps">
            <div class="p-toast-message-block w-100 d-flex align-items-start position-relative user-select-none"
                :class="{ 'cursor-pointer': slotProps.message.onClick && !slotProps.message.onClick_class }">
                <span class="p-toast-message-icon me-2">
                    <i :class="getIcon(slotProps.message.severity)" class="text-xl"></i>
                </span>
                <div class="flex-grow-1">
                    <span class="p-toast-summary">{{ slotProps.message.summary }}</span>
                    <div v-if="slotProps.message.html" class="p-toast-detail" v-html="slotProps.message.html"></div>
                    <div v-else-if="slotProps.message.allowHTML && slotProps.message.detail" class="p-toast-detail"
                        v-html="slotProps.message.detail"></div>
                    <div v-else class="p-toast-detail">{{ slotProps.message.detail }}</div>
                </div>
                <!-- Simple bottom progress bar -->
                <ToastProgressBar :message="slotProps.message"
                    :color="getProgressBarColor(slotProps.message.severity)" />
            </div>
        </template>
    </Toast>
</template>

<script setup>
import {
    ref,
    onMounted,
    onBeforeUnmount,
    nextTick,
    getCurrentInstance,
} from "vue";
import ToastProgressBar from './ToastProgressBar.vue';

const toastPosition = ref(window.innerWidth <= 768 ? "center" : "bottom-left");

const updateToastPosition = () => {
    toastPosition.value = window.innerWidth <= 768 ? "center" : "bottom-left";
};

/**
 * Handle click events on the toast messages
 * A click on the close button will not trigger the onClick handler
 * A Toast can be given an onClick handler and an optional onClick_class to restrict the clickable area
 * to elements with that class
 * 
 * @param event 
 */
const handleClick = (event) => {
    console.log('Toast clicked event:', event);

    //If this was a click on the close button, don't proceed to call onClick handler
    if (event.originalEvent && event.originalEvent.target) {
        const target = event.originalEvent.target;
        if (target.classList.contains('p-toast-close-button') || target.closest('.p-toast-close-button')) {
            console.log('Click was on the close button, not invoking onClick handler.');
            return;
        }
    }

    //If there is an onClick handler in the message, call it
    if (event.message.onClick) {
        //If onClick_class is defined, check if the originalEvent target has that class
        if (event.message.onClick_class) {
            const target = event.originalEvent.target;
            if (!target.classList.contains(event.message.onClick_class) && !target.closest(`.${event.message.onClick_class}`)) {
                console.log(`Click target does not match onClick_class '${event.message.onClick_class}', not invoking onClick handler.`);
                return;
            }
        }

        event.message.onClick(event);
    } else {
        console.log('No onClick handler defined for this toast message.');
    }
    // console.log('Message object:', event.message);
    // console.log('Summary:', event.message.summary);
    // console.log('Detail:', event.message.detail);
    // console.log('Severity:', event.message.severity);
    // console.log('Custom allowHTML:', event.message.allowHTML);
};

const getIcon = (severity) => {
    switch (severity) {
        case "success":
            return "fas fa-fw fa-check-circle text-success";
        case "info":
            return "fas fa-fw fa-info-circle text-info";
        case "warn":
            return "fas fa-fw fa-exclamation-triangle text-warning";
        case "error":
            return "fas fa-fw fa-times-circle text-danger";
        default:
            return "fas fa-fw fa-info-circle";
    }
};

const getProgressBarColor = (severity) => {
    // Use CSS custom properties that reference SCSS variables
    const computedStyle = getComputedStyle(document.documentElement);

    switch (severity) {
        case "success":
            return computedStyle.getPropertyValue('--brand-success').trim() || "#2ab27b";
        case "info":
            return computedStyle.getPropertyValue('--brand-info').trim() || "#8eb4cb";
        case "warn":
            return computedStyle.getPropertyValue('--brand-red').trim() || "#cf2d30";
        case "error":
            return computedStyle.getPropertyValue('--brand-danger').trim() || "#e86115";
        default:
            return computedStyle.getPropertyValue('--brand-primary').trim() || "#2E79C6";
    }
};

const instance = getCurrentInstance();

onMounted(() => {
    nextTick(() => {
        if (instance && instance.proxy && instance.proxy.$toast) {
            window.$toast = instance.proxy.$toast;
        }
    });
    window.addEventListener("resize", updateToastPosition);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", updateToastPosition);
});
</script>
